import React, { useState, useCallback, useMemo } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';

const FontLoader = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&display=swap');
  body {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: ${p => p.theme.bg};
  }
`;

import ConfirmStep from './ConfirmStep';
import OtpStep from '../OAuthFlow/shared/OtpStep';
import TezLabLogo from '../../shared/TezLabLogo';
import SideAppLogo, { SideAppFullLogo } from '../../shared/SideAppLogo';
import buildThemeFromBranding from '../../shared/brandingTheme';

const STEPS = { CONFIRM: 'confirm', OTP: 'otp', COMPLETE: 'complete', CANCELLED: 'cancelled', ERROR: 'error' };

const Page = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.pageBg};
  color: ${p => p.theme.text};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  padding-bottom: calc(24px + 10vh);
  font-family: ${p => p.theme.fontFamily};
`;

const Container = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Logo = styled.div`
  margin-bottom: 14px;
`;

const LogoBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 28px;
`;

const Card = styled.div`
  width: 100%;
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radiusLg};
  padding: 40px;

  @media (max-width: 640px) {
    max-width: 100%;
    padding: 24px;
  }
`;

const Heading = styled.h1`
  font-size: 24px;
  font-weight: 500;
  color: ${p => p.theme.text};
  margin-bottom: 12px;
  text-align: center;
`;

const Message = styled.div`
  text-align: center;
  color: ${p => p.theme.textSecondary};
  font-size: 15px;
  line-height: 24px;
`;

const ResolutionList = styled.ol`
  color: ${p => p.theme.textSecondary};
  font-size: 15px;
  line-height: 24px;
  padding-left: 20px;
  margin: 8px 0 0 0;
  text-align: left;

  li { margin-bottom: 12px; }
  li:last-child { margin-bottom: 0; }
`;

const PoweredBy = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: ${p => p.theme.textTertiary};
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.7;

  svg { opacity: 0.9; }
`;

const AppName = styled.div`
  color: ${p => p.theme.text};
  font-size: 18px;
  font-weight: 600;
  margin-top: 12px;
  margin-bottom: 6px;
  text-align: center;
`;

export default function SsoClaimApp(props) {
  const { branding, claimToken, prefilledName, prefilledEmail, csrfToken } = props;

  const theme = useMemo(function() { return buildThemeFromBranding(branding); }, [branding]);

  const [step, setStep] = useState(STEPS.CONFIRM);
  const [name, setName] = useState(prefilledName || '');
  const [email, setEmail] = useState(prefilledEmail || '');
  const [publicKey, setPublicKey] = useState(null);
  const [otpSessionId, setOtpSessionId] = useState(null);
  const [resendAt, setResendAt] = useState(null);
  const [errorHeading, setErrorHeading] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorResolutionIntro, setErrorResolutionIntro] = useState(null);
  const [errorResolutions, setErrorResolutions] = useState([]);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    'X-CSRF-Token': csrfToken,
  }), [csrfToken]);

  const onOtpSent = useCallback(function(data) {
    setPublicKey(data.public_key);
    setOtpSessionId(data.otp_session_id);
    setResendAt(data.resend_allowed_at);
    setStep(STEPS.OTP);
  }, []);

  const onVerify = useCallback(async (encryptedOtp) => {
    const response = await fetch('/oauth/sso/claim/' + claimToken + '/verify_otp', {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ otp_session_id: otpSessionId, encoded_otp: encryptedOtp }),
    });
    const data = await response.json();

    if (response.ok && data.status === 'complete') {
      setStep(STEPS.COMPLETE);
      if (data.redirect_to) {
        window.location = data.redirect_to;
      }
      return;
    }
    if (response.status === 409) {
      setErrorHeading(data.heading || null);
      setErrorMessage(data.message || 'This email is already associated with another account.');
      setErrorResolutionIntro(data.resolution_intro || null);
      setErrorResolutions(Array.isArray(data.resolutions) ? data.resolutions : []);
      setStep(STEPS.ERROR);
      return;
    }
    throw new Error(data.message || 'Invalid code. Please try again.');
  }, [claimToken, otpSessionId, headers]);

  const onResend = useCallback(async () => {
    const response = await fetch('/oauth/sso/claim/' + claimToken + '/send_otp', {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ email: email, name: name }),
    });
    const data = await response.json();
    if (response.ok) {
      setPublicKey(data.public_key);
      setOtpSessionId(data.otp_session_id);
      setResendAt(data.resend_allowed_at);
      return;
    }
    throw new Error(data.message || 'Could not resend code.');
  }, [claimToken, email, name, headers]);

  const onCancel = useCallback(async function() {
    try {
      await fetch('/oauth/sso/claim/' + claimToken + '/cancel', {
        method: 'POST',
        headers: headers(),
        body: '{}',
      });
    } catch (err) {
      // Best-effort — still show the cancelled screen even if the POST failed.
    }
    setStep(STEPS.CANCELLED);
  }, [claimToken, headers]);

  const renderStep = function() {
    switch (step) {
      case STEPS.CONFIRM:
        return (
          <ConfirmStep
            claimToken={claimToken}
            name={name}
            setName={setName}
            email={email}
            setEmail={setEmail}
            headers={headers}
            onOtpSent={onOtpSent}
            onCancel={onCancel}
          />
        );
      case STEPS.OTP:
        return (
          <OtpStep
            email={email}
            publicKey={publicKey}
            resendAt={resendAt}
            onVerify={onVerify}
            onResend={onResend}
            onCancel={onCancel}
          />
        );
      case STEPS.COMPLETE:
        return (
          <div>
            <Heading>You&apos;re all set!</Heading>
            <Message>Redirecting you back to the app&hellip;</Message>
          </div>
        );
      case STEPS.CANCELLED:
        return (
          <div>
            <Heading>Sign-in cancelled</Heading>
            <Message>You can close this window.</Message>
          </div>
        );
      case STEPS.ERROR:
        return (
          <div>
            <Heading>{errorHeading || 'Something went wrong'}</Heading>
            <Message>{errorMessage}</Message>
            {errorResolutionIntro ? (
              <Message style={{ marginTop: '16px', textAlign: 'left' }}>{errorResolutionIntro}</Message>
            ) : null}
            {errorResolutions.length > 0 ? (
              <ResolutionList>
                {errorResolutions.map((r, i) => <li key={i}>{r}</li>)}
              </ResolutionList>
            ) : null}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <FontLoader />
      <Page>
        <Container>
          <LogoBlock>
            {branding && branding.fullLogoKey ? (
              <Logo><SideAppFullLogo fullLogoKey={branding.fullLogoKey} /></Logo>
            ) : (
              <>
                <Logo>
                  {branding && (branding.logoImageUrl || branding.logoKey)
                    ? <SideAppLogo logoImageUrl={branding.logoImageUrl} logoKey={branding.logoKey} alt={branding.appName} />
                    : <TezLabLogo />}
                </Logo>
                {branding && branding.showPoweredBy && branding.appName && (
                  <AppName>{branding.appName}</AppName>
                )}
                {branding && branding.showPoweredBy && (
                  <PoweredBy>
                    <span>powered by</span>
                    <TezLabLogo height={22} fill={theme.textTertiary} />
                  </PoweredBy>
                )}
              </>
            )}
          </LogoBlock>
          <Card>{renderStep()}</Card>
        </Container>
      </Page>
    </ThemeProvider>
  );
}
